/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  Input,
  OnInit,
  ViewChild,
  ElementRef,
  Self,
  Optional,
  Renderer2,
  AfterViewInit,
  EventEmitter,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
  NgControl,
  NgForm,
  NgModel,
} from '@angular/forms';
import { XdaStringsService } from '../../services/xda-strings.service';
import { EmailService } from '../../services/email.service';

@Component({
  selector: 'xrx-input',
  templateUrl: './xrx-input.component.html',
  styleUrls: ['./xrx-input.component.less'],
  host: {
    '(document:submit)': 'onSubmit($event)',
  },
  standalone: false,
})
export class XrxInputComponent
  implements OnInit, AfterViewInit, ControlValueAccessor
{
  @ViewChild('input', { static: false }) input: ElementRef;

  @Input() type: string;
  @Input() id: string;
  @Input() name: string;
  @Input() label: string;
  @Input() pattern: string | RegExp;
  @Input() placeholder: string;
  @Input() error: string;
  @Input() patternerror: string;
  @Input() compareto: string;
  @Input() compareCase = true;
  @Input() matcherror: string;
  @Input() minValue: number;
  @Input() maxValue: number;
  @Input() minlength: number;
  @Input() minlengtherror: string;
  @Input() maxlength: number;
  @Input() maxlengtherror: string;
  @Input() required: boolean;
  @Input() autofocus: boolean;
  @Input() ishalf: boolean;
  @Input() isMfd: boolean;
  @Input() viewPassword: boolean;
  @Input() classes: string;
  @Input() noValidateError: string;
  @Input() isFileName: boolean;
  @Input() helpBlock: string;
  @Input() readonly: boolean;
  @Input() autocomplete: string;
  @Input() set addDateTime(value: boolean) {
    this._addDateTime = value;
    this.valueChanges();
  }
  get addDateTime() {
    return this._addDateTime;
  }
  @Input() set fileExtension(value: string) {
    this._fileExtension = value;
    this.updateExtension();
  }
  get fileExtension() {
    return this._fileExtension;
  }

  @Output() isValid = new EventEmitter<boolean>();
  @Output() inputFocused = new EventEmitter<boolean>();
  @Output() inputBlurred = new EventEmitter<boolean>();
  @Output() inputClicked = new EventEmitter<any>();
  @Output() inputTapped = new EventEmitter<any>();

  disabled: boolean;
  datetime: string;
  timeoutId: any = null;
  onSubmitError = false;
  showPassword = false;
  _fileExtension: string;
  _addDateTime: boolean;
  isFocussed = false;
  msie: any; // holds major version number for IE, or NaN if UA is not IE.
  _readonly = false;

  constructor(
    @Self() @Optional() public ngControl: NgControl,
    private xdaStringsService: XdaStringsService,
    private renderer: Renderer2,
    private form: NgForm,
    private emailService: EmailService,
  ) {
    this.ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    this.setupFields();
    const control = this.ngControl.control;
    const validators: ValidatorFn[] = control?.validator
      ? [control.validator]
      : [];
    if (this.required) {
      validators.push(Validators.required);
    }
    if (this.pattern) {
      validators.push(Validators.pattern(this.pattern));
    }
    if (this.type === 'email') {
      validators.push(Validators.pattern(this.emailService.emailRegex));
    }
    if (this.compareto) {
      validators.push(this.compareValidator);
    }
    control?.valueChanges.subscribe(() => this.valueChanges());
    control?.setValidators(validators);
    control?.updateValueAndValidity();

    // save original value of readonly to _readonly before changing it on MFD
    this._readonly = this.readonly;

    // if isMFD set readonly to true. This prevents the keyboard on Versa from have extra 'tab' keys at the top of the keyboard
    if (this.isMfd) {
      this.readonly = true;
    }
  }

  setupFields() {
    if (this.placeholder) {
      const text = this.xdaStringsService.strings[this.placeholder];
      if (text) {
        this.placeholder = text;
      }
    } else {
      this.placeholder = '';
    }

    if (this.type === 'password' && this.patternerror && !this.pattern) {
      // AppGallery requirement: 12-64 characters long, one uppercase letter, one lowercase letter, one number and one special character
      this.pattern =
        '(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*(),.?""{}|<>])[A-Za-z\\d!@#$%^&*(),.?""{}|<>]{12,64}';
    }
    this.datetime = this.xdaStringsService.strings['SDE_DATE_TIME4'];
  }

  updateExtension() {
    if (this.input) {
      const name: string = this.input.nativeElement.value;
      const extensionLoc = name.lastIndexOf('.');
      if (extensionLoc > 0) {
        const old = name.substring(extensionLoc + 1);
        this.input.nativeElement.value = name.replace(old, this.fileExtension);
      }
    }
  }

  insertDateTime() {
    let value = this.input.nativeElement.value;
    const dateAndTimeAlreadyAdded =
      value.lastIndexOf('[') != -1 && value.lastIndexOf('.') != -1;
    // If value is empty
    if (value == '' || (dateAndTimeAlreadyAdded && this.addDateTime)) {
      return;
    } else if (dateAndTimeAlreadyAdded && !this.addDateTime) {
      // if addDateTime was turned off, remove it
      value = value.substr(0, value.lastIndexOf('['));
    }

    // If value ends with the extension, remove it
    if (value.endsWith(`.${this.fileExtension}`)) {
      value = value.substr(0, value.lastIndexOf(`.${this.fileExtension}`));
    }

    if (this.addDateTime) {
      this.input.nativeElement.value = `${value}${this.datetime}.${this.fileExtension}`;
    } else {
      this.input.nativeElement.value = `${value}.${this.fileExtension}`;
    }
  }

  ngAfterViewInit() {
    if (this.classes) {
      const arr = this.classes.split(' ');
      arr.forEach((c) => {
        this.renderer.addClass(this.input.nativeElement, c);
      });
    }
    if (this.autofocus) {
      this.focus();
    }
    if (this.isFileName && this.fileExtension) {
      setTimeout(() => {
        this.insertDateTime();
      }, 100);
    }

    if (this.msie <= 11) {
      setTimeout(() => {
        this.ieSetModel();
      }, 100);
    }

    const ngModel = this.ngControl as NgModel;
    if (ngModel.viewModel) {
      setTimeout(() => {
        const control = this.ngControl.control;
        control?.markAsDirty();
        control?.markAsTouched();
        control?.updateValueAndValidity();
      }, 1);
    }
  }

  ieSetModel() {
    // to fix 10779 IE Defect
    if (this.input.nativeElement.value != this.ngControl.control?.value) {
      this.ngControl.control?.setValue(this.input.nativeElement.value);
    }
  }

  writeValue(obj: any): void {
    // setTimeout because somewhere during initilization the default value is being erased
    setTimeout(() => {
      if (obj !== undefined) {
        this.input.nativeElement.value = obj;
        // if enterting text greater than max length trim and show max length error
        if (
          this.maxlength > 0 &&
          this.input.nativeElement.value.length > this.maxlength
        ) {
          this.ngControl.control?.setValue(
            this.input.nativeElement.value.substring(0, this.maxlength),
          );
          this.showMaxLengthError();
        }
      }
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onChange(_event: string) {}

  onTouched() {}

  onKeyPress(event: KeyboardEvent) {
    if (
      this.type === 'number' &&
      !(!isNaN(parseFloat(event.key)) && isFinite(Number(event.key)))
    ) {
      return false;
    }
    // Handle if user tries to enter text past max length
    if (
      this.maxlength > 0 &&
      this.input.nativeElement.value.length == this.maxlength &&
      !event.ctrlKey
    ) {
      this.showMaxLengthError();
      return false; // ignore keys beyond max length
    }
    return true;
  }

  showMaxLengthError() {
    this.ngControl.control?.setErrors({ maxlength: true });
    this.ngControl.control?.markAsDirty();
    this.ngControl.control?.markAsTouched();
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
    }
    this.timeoutId = setTimeout(() => {
      if (this.ngControl && this.ngControl.control?.hasError('maxlength')) {
        if (this.ngControl.control.errors) {
          delete this.ngControl.control.errors['maxlength'];
        }
        this.ngControl.control.markAsPristine();
        this.ngControl.control.updateValueAndValidity();
        this.timeoutId = null;
      }
    }, 3000);
  }

  onFocus() {
    this.isFocussed = true;
    if (this.isFileName && this.fileExtension) {
      const name = this.input.nativeElement.value;
      const index = name.lastIndexOf('.');
      this.input.nativeElement.value =
        index != -1 ? name.substr(0, name.lastIndexOf('.')) : name;
      if (this.addDateTime) {
        this.input.nativeElement.value = name.substr(0, name.lastIndexOf('['));
      }
      setTimeout(() => {
        this.input.nativeElement.select();
      });
    }
    // if isMfd and if the original value for readonly wasn't false enable the input for text entry when the input is brought into focus
    if (this.isMfd && !this._readonly) {
      this.readonly = false;
    }

    // trigger inputFocused event
    this.inputFocused.emit(this.input.nativeElement);
  }

  onBlur() {
    this.isFocussed = false;
    if (this.isFileName && this.fileExtension) {
      if (this.input.nativeElement.value) {
        this.insertDateTime();
      }
    }

    // trigger inputBlurred event
    this.inputBlurred.emit();

    // if isMfd and if the original value for readonly wasn't false make input readonly when the input loses focus
    if (this.isMfd && !this._readonly) {
      this.readonly = true;
    }
  }

  onClick(event: Event) {
    this.inputClicked.emit(event);
  }

  onTap(event: Event) {
    this.inputTapped.emit(event);
  }

  valueChanges() {
    this.clearCustomError();
    if (!this.isFocussed && this.isFileName && this.fileExtension) {
      setTimeout(() => {
        this.insertDateTime();
      }, 100);
    }

    this.isValid.emit(!this.ngControl.control?.invalid);
  }

  clearCustomError() {
    this.error = '';
    this.noValidateError = '';
    this.onSubmitError = false;
  }

  togglePassword() {
    if (!this.disabled) {
      this.showPassword = !this.showPassword;
      if (this.showPassword) {
        this.type = 'text';
      } else {
        this.type = 'password';
      }
    }
  }

  compareValidator: ValidatorFn = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const field1 = control.value || '';
    let field2 = '';
    let field2Control: AbstractControl | null = null;
    if (control.parent) {
      field2 = control.parent?.get(this.compareto)?.value;
      if (control.parent?.get(this.compareto)) {
        field2Control = control.parent.get(this.compareto)!;
      }
      if (field2Control !== null) {
        field2Control?.valueChanges.subscribe(() =>
          this.watchField(field2Control!),
        );
      }
    }
    const match = this.compareCase
      ? field1 == field2
      : field1?.toLowerCase() == field2?.toLowerCase();
    return !match &&
      field2Control &&
      field2Control.touched &&
      field2Control.value
      ? { match: true }
      : null;
  };

  watchField(control: AbstractControl) {
    if (
      this.ngControl.control?.value &&
      this.ngControl.control.touched &&
      this.compareCase
        ? this.ngControl.control.value !== control.value
        : this.ngControl.control?.value?.toLowerCase() !==
          control.value?.toLowerCase()
    ) {
      this.ngControl.control?.setErrors({ match: true });
    } else {
      if (
        this.ngControl.control?.errors &&
        this.ngControl.control.errors['match']
      ) {
        delete this.ngControl.control.errors['match'];
        this.ngControl.control?.updateValueAndValidity();
      }
    }
  }

  registerOnValidatorChange?(fn: () => void): void {
    throw new Error('Method not implemented.');
  }

  blur() {
    this.input.nativeElement.blur();
  }

  focus() {
    this.input.nativeElement.focus();
  }

  isErrorState(): boolean {
    return (
      (this.ngControl.control?.invalid &&
        this.ngControl.control?.dirty &&
        !this.ngControl.control?.untouched) ??
      false
    );
  }

  hasAnyError(): boolean {
    return (
      this.ngControl.control?.errors?.['required'] ||
      (this.ngControl.control?.errors?.['pattern'] && this.patternerror) ||
      (this.ngControl.control?.errors?.['minlength'] && this.minlengtherror) ||
      this.ngControl.control?.errors?.['maxlength'] ||
      (this.ngControl.control?.errors?.['pattern'] && this.type === 'email') ||
      this.ngControl.control?.errors?.['match'] ||
      this.ngControl.control?.errors?.['customerror']
    );
  }

  onSubmit($event: Event) {
    if (this.form.submitted && this.ngControl.control?.invalid) {
      this.onSubmitError = true;
    }
  }
}
